import axios from "axios";
import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  ISla,
  ISlaActivity,
  ISlaWorkflowCondition,
  ISlaCreatePayload,
  ISlaUpdatePayload,
  ISlaValidationError,
  ISlaWorkflowCoverage,
  ITeamSla,
  TSlaErrorCode,
} from "@/types/sla";

/** Standard cursor-paginated envelope for the SLA list. */
export interface ISlaListPage {
  results: ISla[];
  next_cursor: string;
  prev_cursor: string;
  next_page_results: boolean;
  prev_page_results: boolean;
  total_count: number;
  count: number;
  /** True when a catch-all SLA exists. Deliberately unread since NG-15 D3.4: a
   *  scoped SLA now coexists with the catch-all (which is what "residual" means),
   *  so it no longer gates creation; a *second* catch-all is rejected on write
   *  with `sla_catch_all_conflict`. The server keeps returning it until PR 12 so
   *  a bundle deployed before this one keeps its old gating. */
  has_catch_all: boolean;
}

export interface ISlaListQuery {
  cursor?: string | null;
  q?: string;
  order_by?: string;
  per_page?: number;
  /** Only SLAs whose live conditions cover this workflow — answers "which SLAs
   *  apply to this form?" for the Form Details SLAs field. */
  workflow_id?: string;
}

/** Cursor-paginated envelope for an SLA's activity feed (newest-first). */
export interface ISlaActivityPage {
  results: ISlaActivity[];
  next_cursor: string;
  next_page_results: boolean;
  total_count: number;
  count: number;
}

/** The team SLA endpoint's own maximum (`TeamSLAListEndpoint.max_per_page`);
 *  asking for more is silently clamped, which would make the walk below take
 *  more round trips than it thinks. */
const TEAM_SLA_PAGE_SIZE = 100;

const SLA_ERROR_CODES: TSlaErrorCode[] = [
  "sla_conditions_overlap",
  "sla_catch_all_conflict",
  "sla_invalid_pause_status",
  "sla_inactive_category",
  "sla_category_conditions_retired",
  "sla_inactive_workflow",
  "sla_unmanaged_workflow",
  "sla_lead_catch_all_forbidden",
  "sla_no_conditions",
  "sla_last_workflow_removal",
  "sla_invalid_condition_state",
  "sla_conflicting_condition_input",
  "rbac_denied",
];

/**
 * Normalize a thrown error into an {@link ISlaValidationError}. The SLA API
 * returns `{"error": message, "code": code}` for the documented validation
 * failures on 400, and the workflow-side assignment endpoint reuses that
 * envelope for its 403 (`rbac_denied`) — both are shaped for inline display, so
 * both statuses are read here. Anything else (network, 500, DRF's default
 * shape) yields a `null` code so callers fall back to a generic toast. The
 * caught value is preserved by the caller's logging.
 */
export function toSlaValidationError(err: unknown): ISlaValidationError {
  if (axios.isAxiosError(err) && (err.response?.status === 400 || err.response?.status === 403)) {
    const data = err.response.data as { error?: string; code?: string } | undefined;
    const code =
      data?.code && (SLA_ERROR_CODES as string[]).includes(data.code)
        ? (data.code as TSlaErrorCode)
        : null;
    if (data?.error) return { code, message: data.error };
  }
  return { code: null, message: err instanceof Error ? err.message : String(err) };
}

/** One page of the active workflows walk (`listActiveWorkflows`). */
const WORKFLOW_PAGE_SIZE = 100;

function slaListParams(options: ISlaListQuery, perPage: number): URLSearchParams {
  const params = new URLSearchParams();
  params.set("per_page", String(perPage));
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.q) params.set("q", options.q);
  if (options.order_by) params.set("order_by", options.order_by);
  if (options.workflow_id) params.set("workflow_id", options.workflow_id);
  return params;
}

export class SlaService extends ApiService {
  async listPage(
    slug: string,
    options: ISlaListQuery = {},
    config?: ApiRequestConfig,
  ): Promise<ISlaListPage> {
    const params = slaListParams(options, options.per_page ?? 50);
    return this.get(`/api/workspaces/${slug}/slas/?${params.toString()}`, config);
  }

  /**
   * Every SLA matching `options`, walking the cursor: for a surface using the
   * list as an OPTION SET rather than a scrollable table (the Form Details SLAs
   * picker, the report builder's SLA filter), where a row past the first page
   * is not "not loaded yet", it is unselectable.
   */
  async listAll(
    slug: string,
    options: Omit<ISlaListQuery, "cursor" | "per_page"> = {},
    config?: ApiRequestConfig,
  ): Promise<ISla[]> {
    return this.getAllPages<ISla>(
      ({ cursor, perPage }) => {
        const params = slaListParams({ ...options, cursor }, perPage);
        return `/api/workspaces/${slug}/slas/?${params.toString()}`;
      },
      50,
      config,
    );
  }

  /**
   * The active workflows an SLA can be scoped to (NG-15 §5.2), by name. The
   * picker searches client-side, so the whole option set is fetched, walking
   * the cursor rather than trusting one page: a condition you cannot see is a
   * condition you cannot fix.
   */
  async listActiveWorkflows(
    slug: string,
    config?: ApiRequestConfig,
  ): Promise<ISlaWorkflowCondition[]> {
    const forms = await this.getAllPages<ISlaWorkflowCondition>(
      ({ cursor, perPage }) => {
        const params = new URLSearchParams();
        params.set("per_page", String(perPage));
        params.set("is_active", "true");
        params.set("order_by", "name");
        if (cursor) params.set("cursor", cursor);
        return `/api/workspaces/${slug}/intake/forms/?${params.toString()}`;
      },
      WORKFLOW_PAGE_SIZE,
      config,
    );
    return forms.map((form) => ({ id: form.id, name: form.name }));
  }

  /**
   * Every SLA relevant to one team, labelled by `relation` (NG-15 §3.3).
   *
   * Walks the cursor to exhaustion rather than returning one page, matching
   * `WorkspaceTeamService.list`. The tab is not a paginated surface: it groups
   * the rows by relation and is also the only place a detail deep link can
   * resolve a row's `relation` from, so a truncated first page both hides
   * rows and mislabels the ones it keeps — the `residual` row sorts last and
   * would be the first casualty. The server caps a page at 100.
   */
  async listAllTeamSlas(
    slug: string,
    teamId: string,
    config?: ApiRequestConfig,
  ): Promise<ITeamSla[]> {
    return this.getAllPages<ITeamSla>(
      ({ cursor, perPage }) => {
        const params = new URLSearchParams();
        params.set("per_page", String(perPage));
        if (cursor) params.set("cursor", cursor);
        return `/api/workspaces/${slug}/teams/${teamId}/slas/?${params.toString()}`;
      },
      TEAM_SLA_PAGE_SIZE,
      config,
    );
  }

  /** Which SLA already claims each workflow in the workspace (NG-208).
   *
   *  One row per claimed workflow, so an empty list means nothing is claimed —
   *  the client never has to diff it against the workflow bank. */
  async listWorkflowCoverage(
    slug: string,
    config?: ApiRequestConfig,
  ): Promise<ISlaWorkflowCoverage[]> {
    const response = await this.get<{ results: ISlaWorkflowCoverage[] }>(
      `/api/workspaces/${slug}/slas/workflow-coverage/`,
      config,
    );
    return response.results ?? [];
  }

  async get_(slug: string, slaId: string, config?: ApiRequestConfig): Promise<ISla> {
    return this.get(`/api/workspaces/${slug}/slas/${slaId}/`, config);
  }

  async create(slug: string, data: ISlaCreatePayload): Promise<ISla> {
    return this.post(`/api/workspaces/${slug}/slas/`, data);
  }

  async update(slug: string, slaId: string, data: ISlaUpdatePayload): Promise<ISla> {
    return this.patch(`/api/workspaces/${slug}/slas/${slaId}/`, data);
  }

  async remove(slug: string, slaId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/slas/${slaId}/`);
  }

  /**
   * Replace the set of SLAs covering one workflow — the workflow-side mirror of
   * editing an SLA's conditions, used by the Form Details SLAs field.
   *
   * A dedicated endpoint rather than a field on the form PATCH: the PATCH lands
   * on the intake serializer, and writing SLA conditions from there would skip
   * the SLA aggregate (no measurement backfill, no activity row). Rejections use
   * the SLA `{error, code}` envelope, so {@link toSlaValidationError} shapes them
   * for inline display.
   */
  async setWorkflowSlas(slug: string, formId: string, slaIds: string[]): Promise<string[]> {
    const response = await this.put<{ sla_ids: string[] }>(
      `/api/workspaces/${slug}/intake/forms/${formId}/slas/`,
      { sla_ids: slaIds },
    );
    return response.sla_ids;
  }

  /** Fetch a page of an SLA's activity feed (newest-first). Pass `cursor` from a
   *  previous page's `next_cursor` to load older entries. */
  async listActivityPage(
    slug: string,
    slaId: string,
    options: { cursor?: string | null; per_page?: number } = {},
    config?: ApiRequestConfig,
  ): Promise<ISlaActivityPage> {
    const params = new URLSearchParams();
    params.set("per_page", String(options.per_page ?? 50));
    if (options.cursor) params.set("cursor", options.cursor);
    return this.get(
      `/api/workspaces/${slug}/slas/${slaId}/activities/?${params.toString()}`,
      config,
    );
  }
}
